import { useEffect, useMemo, useState } from "react";
import { Search, Plus, Trash2, ArrowUpDown, ArrowUp, ArrowDown, UserPlus, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "../api";
import { useAuth } from "../auth";
import CompanyScopeBar from "../components/CompanyScopeBar";
import ProjectDetail from "./ProjectDetail";

const isEmptyEmp = (p) => !p.employer_short || p.employer_short === "—" || p.employer_short === "-";

export default function Projects({ onNew, initialProject, company, setCompany }) {
  const { can } = useAuth();
  const editable = can("admin", "project_manager");
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [q, setQ] = useState("");
  const [fEmp, setFEmp] = useState("");
  const [fCity, setFCity] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [onlyEmpty, setOnlyEmpty] = useState(false);
  const [assign, setAssign] = useState({});   // projectId -> selected employer_short
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [selected, setSelected] = useState(initialProject || null);

  const load = () => api.get(`/projects${company ? `?company=${encodeURIComponent(company)}` : ""}`).then(r => setItems(r.data)).catch(() => toast.error("Projeler yüklenemedi"));
  useEffect(() => { load(); }, [company]);
  useEffect(() => { api.get("/employers").then(r => setEmps(r.data)).catch(() => {}); }, []);

  const emptyCount = useMemo(() => items.filter(isEmptyEmp).length, [items]);

  const saveEmployer = async (id) => {
    const val = assign[id];
    if (!val) return toast.error("Önce bir işveren seçin");
    try {
      await api.put(`/projects/${id}/employer`, { employer_short: val });
      toast.success("İşveren atandı");
      setAssign((a) => { const n = { ...a }; delete n[id]; return n; });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const { employers, cities, statuses } = useMemo(() => {
    const u = (key) => [...new Set(items.map(p => p[key]).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"));
    return { employers: u("employer_short"), cities: u("city"), statuses: u("status") };
  }, [items]);

  const toggleSort = (key) => setSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const SortIco = ({ col }) => sort.key !== col ? <ArrowUpDown size={12} /> : (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    let arr = items.filter(p =>
      (!q || p.name?.toLowerCase().includes(ql) || p.employer_short?.toLowerCase().includes(ql) || p.investor_short?.toLowerCase().includes(ql)) &&
      (!fEmp || p.employer_short === fEmp) &&
      (!fCity || p.city === fCity) &&
      (!fStatus || p.status === fStatus) &&
      (!onlyEmpty || isEmptyEmp(p))
    );
    const k = sort.key;
    arr = [...arr].sort((a, b) => {
      const va = (a[k] ?? "").toString(), vb = (b[k] ?? "").toString();
      return sort.dir === "asc" ? va.localeCompare(vb, "tr", { numeric: true }) : vb.localeCompare(va, "tr", { numeric: true });
    });
    return arr;
  }, [items, q, fEmp, fCity, fStatus, onlyEmpty, sort]);

  const remove = async (id) => {
    if (!window.confirm("Bu proje çöp kutusuna taşınsın mı? (Geri yükleyebilirsiniz)")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Proje çöp kutusuna taşındı"); load();
  };

  if (selected) return <ProjectDetail id={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <main className="content" data-testid="page-projects">
      <div className="page-head">
        <div>
          <span className="section-kicker">TÜM PROJELER</span>
          <h1>Projeler ({filtered.length}{filtered.length !== items.length ? ` / ${items.length}` : ""})</h1>
          <p>İşveren-yatırımcı bazında tüm projeleri yönetin.</p>
        </div>
        {can("admin", "project_manager") && <button className="new-btn" onClick={onNew} data-testid="add-project-button"><Plus size={17} /> Yeni Proje</button>}
      </div>

      <CompanyScopeBar company={company} setCompany={setCompany} />

      <div className="filter-bar">
        <div className="search-wrap">
          <Search size={15} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proje veya işveren ara..." data-testid="projects-search-input" />
        </div>
        <select className="filter-select" value={fEmp} onChange={(e) => setFEmp(e.target.value)} data-testid="projects-filter-employer">
          <option value="">Tüm işverenler</option>
          {employers.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <select className="filter-select" value={fCity} onChange={(e) => setFCity(e.target.value)} data-testid="projects-filter-city">
          <option value="">Tüm lokasyonlar</option>
          {cities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="filter-select" value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="projects-filter-status">
          <option value="">Tüm durumlar</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {editable && emptyCount > 0 && (
          <button className={`chip-toggle ${onlyEmpty ? "active" : ""}`} onClick={() => setOnlyEmpty(v => !v)} data-testid="projects-filter-empty-employer">
            <UserPlus size={14} /> İşvereni boş ({emptyCount})
          </button>
        )}
        {(q || fEmp || fCity || fStatus || onlyEmpty) && <button className="ghost-btn" onClick={() => { setQ(""); setFEmp(""); setFCity(""); setFStatus(""); setOnlyEmpty(false); }} data-testid="projects-clear-filters">Temizle</button>}
      </div>

      <section className="panel">
        <div className="project-table" data-testid="projects-table">
          <div className="table-row table-head projects-table-row">
            <button className="th-sort" onClick={() => toggleSort("name")} data-testid="sort-name"><span>PROJE</span> <SortIco col="name" /></button>
            <button className="th-sort" onClick={() => toggleSort("employer_short")} data-testid="sort-employer"><span>İŞVEREN / YATIRIMCI</span> <SortIco col="employer_short" /></button>
            <button className="th-sort" onClick={() => toggleSort("city")} data-testid="sort-city"><span>LOKASYON</span> <SortIco col="city" /></button>
            <button className="th-sort" onClick={() => toggleSort("status")} data-testid="sort-status"><span>DURUM</span> <SortIco col="status" /></button>
            <span>BEDEL / ÖDENEN</span><span>KALAN</span><span></span>
          </div>
          {filtered.map((p, i) => {
            const remaining = (p.agreed_amount || 0) - (p.paid_amount || 0);
            const showAssign = onlyEmpty && editable && isEmptyEmp(p);
            return (
              <div className="table-row projects-table-row" key={p.id} data-testid={`projects-row-${i}`} onClick={() => setSelected(p.id)}>
                <div className="project-name">
                  <div className={`project-badge badge-${i % 3}`}>{(p.name || "").split(" ").map(x => x[0]).join("").slice(0, 2)}</div>
                  <div><b>{p.name}</b><small>#{p.project_no || "-"}</small></div>
                </div>
                {showAssign ? (
                  <div className="inline-assign" onClick={(e) => e.stopPropagation()}>
                    <select value={assign[p.id] || ""} onChange={(e) => setAssign(a => ({ ...a, [p.id]: e.target.value }))} data-testid={`assign-employer-select-${i}`}>
                      <option value="">İşveren seç…</option>
                      {emps.map(e => <option key={e.id} value={e.short_name}>{e.short_name}</option>)}
                    </select>
                    <button className="new-btn sm" onClick={() => saveEmployer(p.id)} data-testid={`assign-employer-save-${i}`}><Check size={13} /></button>
                  </div>
                ) : (
                  <div><b className="text-line">{p.employer_short || "—"}</b><small className="text-line">{p.investor_short || "—"}</small></div>
                )}
                <div><b className="text-line">{p.city || "—"}</b><small className="text-line">{p.district || ""}</small></div>
                <span>
                  <em className={`status ${p.status === "Teklif" ? "status-blue" : p.status === "Tamamlandı" ? "status-green" : "status-amber"}`}>
                    {p.status}
                  </em>
                </span>
                <div className="amount-cell">
                  <b>{money(p.agreed_amount, p.currency)}</b>
                  <small>{money(p.paid_amount, p.currency)}</small>
                </div>
                <b className="margin">{money(remaining, p.currency)}</b>
                {can("admin", "project_manager") ? <button className="row-action danger" onClick={(e) => { e.stopPropagation(); remove(p.id); }} data-testid={`delete-project-${i}`}><Trash2 size={13} /></button> : <span />}
              </div>
            );
          })}
          {!filtered.length && <div className="empty-hint">Proje bulunamadı.</div>}
        </div>
      </section>
    </main>
  );
}
